import type { CargoMetrics } from '../utils/cargoMetrics'

export type FeeUnit =
  | 'FORFAIT'
  | 'PER_DOCUMENT'
  | 'PER_KG'
  | 'PER_TON'
  | 'PER_M3'
  | 'PER_100KG_AIR'   // 1m³ = 166,66 kg (IATA 1:6)
  | 'PER_100KG_LCL'   // 1m³ = 300 kg (LCL 1:3.33)
  | 'PER_WM'          // ton ou m³ (o maior)
  | 'PER_CONTAINER'
  | 'PER_KG_M3'       // densidade (peso ÷ volume) — Fuel Charge aéreo

export interface FeeUnitDef {
  value: FeeUnit
  label: string
  shortLabel: string  // shown next to amount (e.g., "/100kg")
  description?: string
  billable: (m: CargoMetrics) => number
}

export const FEE_UNITS: FeeUnitDef[] = [
  {
    value: 'FORFAIT',
    label: 'Forfait / Fixo',
    shortLabel: 'fixo',
    description: 'Taxa fixa, independente da carga',
    billable: () => 1,
  },
  {
    value: 'PER_DOCUMENT',
    label: 'Por Documento (BL/AWB)',
    shortLabel: '/doc',
    description: 'Aplicada uma vez por documento',
    billable: () => 1,
  },
  {
    value: 'PER_KG',
    label: 'Por Kg',
    shortLabel: '/kg',
    billable: (m) => m.totalWeightKg,
  },
  {
    value: 'PER_TON',
    label: 'Por Tonelada',
    shortLabel: '/ton',
    billable: (m) => m.totalWeightKg / 1000,
  },
  {
    value: 'PER_M3',
    label: 'Por m³',
    shortLabel: '/m³',
    billable: (m) => m.totalCubicMeters,
  },
  {
    value: 'PER_100KG_AIR',
    label: 'Por 100 Kg (Aéreo · 1m³ = 166,66 kg)',
    shortLabel: '/100kg',
    description: 'IATA 1:6 — usa o maior entre peso real e cubado',
    billable: (m) => m.airChargeableKg / 100,
  },
  {
    value: 'PER_100KG_LCL',
    label: 'Por 100 Kg (Marítimo LCL · 1m³ = 300 kg)',
    shortLabel: '/100kg',
    description: 'LCL 1:3.33 — usa o maior entre peso real e cubado',
    billable: (m) => m.lclChargeableKg / 100,
  },
  {
    value: 'PER_WM',
    label: 'Por W/M (tonelada ou m³)',
    shortLabel: '/wm',
    description: 'Marítimo — fatura o maior entre tonelagem e cubagem',
    billable: (m) => m.wmTonOrM3,
  },
  {
    value: 'PER_CONTAINER',
    label: 'Por Container (FCL)',
    shortLabel: '/ctr',
    billable: (m) => m.containerCount,
  },
  {
    value: 'PER_KG_M3',
    label: 'Por Kg/m³ (densidade · Aéreo)',
    shortLabel: '/kg·m³',
    description: 'Multiplica pela densidade da carga (peso total ÷ volume total)',
    billable: (m) => (m.totalCubicMeters > 0 ? m.totalWeightKg / m.totalCubicMeters : m.totalWeightKg),
  },
]

export const FEE_UNIT_MAP: Record<FeeUnit, FeeUnitDef> = FEE_UNITS.reduce(
  (acc, u) => ({ ...acc, [u.value]: u }),
  {} as Record<FeeUnit, FeeUnitDef>
)

export const DEFAULT_FEE_UNIT: FeeUnit = 'FORFAIT'

export function computeFeeSubtotal(
  rate: number,
  unit: FeeUnit | undefined | null,
  cargo: CargoMetrics
): number {
  const def = FEE_UNIT_MAP[unit || DEFAULT_FEE_UNIT]
  const qty = def?.billable(cargo) ?? 1
  return rate * qty
}
